import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import i18next from 'i18next';
import { useSelector } from 'react-redux';

import startCase from 'lodash.startcase';
import Status from './Status';
import {
  getIsReadOnlyUser,
  getIsFullAccessUser,
  getIsPrimaryUser,
} from '../../../store/selectors/accountSelectors';
import { transactionTypesArray } from '../../../util/enum/api/transactionTypes';
import { parseEnumType } from '../../../util/helpers/enumMappers';
import Tooltip from '../../Tooltip/Tooltip';
import useWindowSize from '../../../util/hooks/useIsMobileHook';
import { mediaBelow, pxToRem, pxToRemMd } from '../../../assets/styles/helper';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import { uFlexColumn } from '../../../assets/styles/utility';
import { formatMoneyNumeral } from '../../../util/helpers/numeralHelpers';
import Button from '../../Button/Button';
import { ReactComponent as Caret } from '../../../assets/images/svg/down.svg';
import { pendingTransactionCancel } from '../../Table/Components/PendingTransaction';
import useCheckPermissions from '../../../util/hooks/useCheckPermissions';
import { buttonizeDiv } from '../../../util/buttonizeDiv';

// Every row keeps its status, Cancel button and a chevron-wide space in one
// trailing column as wide as the completed rows' status + chevron columns
// (226 + 24 + 16), so the status icons and Cancel buttons line up whichever kind
// of row it is. Only Target Price Order rows show a chevron in that space.
const PendingTransactionCardContainer = styled.div`
  padding: ${pxToRem(18)} ${pxToRem(24)};
  cursor: pointer;
  display: grid;
  grid-template-columns: 1fr ${pxToRem(266)};
  grid-column-gap: ${pxToRem(24)};
  align-items: center;

  &:nth-child(even) {
    background-color: ${themeColors.colorTableEvenRowBackground};
  }

  &:hover {
    background-color: ${themeColors.colorTableEvenRowHoverBackground};
  }

  ${mediaBelow(variables.breakpoints.bpXl)} {
    padding: ${pxToRemMd(22)} ${pxToRemMd(12)} ${pxToRemMd(22)} ${pxToRemMd(20)};
  }

  ${mediaBelow(variables.breakpoints.bpSm)} {
    grid-template-columns: 1fr auto;
  }
`;

const PendingTransactionCardInfo = styled.div`
  display: flex;
  align-items: center;
`;

const PendingTransactionCardDate = styled.div`
  ${uFlexColumn};
  align-items: center;
  justify-content: center;
  color: ${themeColors.colorTextSecondary};
  margin-right: ${pxToRem(24)};
  font-weight: 600;
  flex-shrink: 0;
`;

const PendingTransactionCardMonth = styled.p`
  text-align: center;
  font-size: ${pxToRem(14)};
  line-height: 1.6;
  color: ${themeColors.colorTextSecondary};
  letter-spacing: 0;
  text-transform: uppercase;

  ${mediaBelow(variables.breakpoints.bpXl)} {
    font-size: ${pxToRemMd(14)};
    line-height: 1.72;
  }
`;

const PendingTransactionCardDay = styled.p`
  text-align: center;
  font-size: ${pxToRem(14)};
  line-height: 1.6;
  letter-spacing: 0;
  color: ${themeColors.colorTextSecondary};
  ${mediaBelow(variables.breakpoints.bpXl)} {
    font-size: ${pxToRemMd(14)};
    line-height: 1.72;
  }
`;

const PendingTransactionCardTitle = styled.div`
  ${uFlexColumn};
  width: 100%;
`;

const PendingTransactionCardAction = styled.p`
  text-align: left;

  ${mediaBelow(variables.breakpoints.bpXl)} {
    line-height: 1.5;
  }
`;

const PendingTransactionCardProduct = styled.p`
  text-align: left;

  ${mediaBelow(variables.breakpoints.bpXl)} {
    line-height: 1.5;
  }
`;

const PendingTransactionCardStatus = styled.div`
  display: flex;
  justify-content: flex-start;

  ${mediaBelow(variables.breakpoints.bpSm)} {
    margin-right: ${pxToRemMd(12)};
    flex-shrink: 0;
    min-width: 0;
  }
`;

const PendingTransactionCardIcon = styled.div`
  flex-shrink: 0;

  button {
    min-width: ${pxToRem(80)};

    /* Button's generic below-bpMd padding (25) overrides the sm size's own (15),
       which made this button 118px wide and crowded the row. */
    ${mediaBelow(variables.breakpoints.bpMd)} {
      padding: ${pxToRemMd(4)} ${pxToRemMd(15)};
    }
  }
`;

// The status and the Cancel button + chevron space share the card's second
// column. The status takes whatever width the actions leave, so its left edge
// stays aligned with the other rows however wide the button is.
const PendingTransactionCardTrailing = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;

  ${PendingTransactionCardStatus} {
    flex: 1 1 0;
    min-width: 0;
  }

  ${mediaBelow(variables.breakpoints.bpSm)} {
    ${PendingTransactionCardStatus} {
      flex: none;
    }
  }
`;

const PendingTransactionCardActions = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: ${pxToRem(24)};

  ${mediaBelow(variables.breakpoints.bpSm)} {
    margin-left: 0;
  }
`;

// Same chevron as the completed rows in this list (RecentTransactionCard). Rows
// without one keep its space, so their Cancel button lines up with the Target
// Price Order rows'.
const PendingTransactionCardArrow = styled.div`
  flex-shrink: 0;
  width: ${pxToRem(16)};
  margin-left: ${pxToRem(8)};

  ${mediaBelow(variables.breakpoints.bpSm)} {
    width: ${pxToRemMd(16)};
  }

  svg {
    color: ${themeColors.colorLabel};
    transform: rotate(-90deg);
  }
`;

const PendingTransactionCard = ({
  month,
  day,
  amount,
  status,
  action,
  product,
  isSegregated,
  segregatedCaption,
  vaultName,
  label,
  canCancel,
  handleModalOpen,
  onCardClick,
  isTriggerOrder,
}) => {
  const windowSize = useWindowSize();
  const isMobile = windowSize.width < 576;
  const { allowAccountChangesPermission } = useCheckPermissions();
  const isReadOnlyUser = useSelector(getIsReadOnlyUser);
  const isFullAccessUser = useSelector(getIsFullAccessUser);
  const isPrimaryUser = useSelector(getIsPrimaryUser);
  const isIneligibleToCancel =
    isReadOnlyUser || (isFullAccessUser && !isPrimaryUser);

  const transactionTypeConst = parseEnumType(transactionTypesArray, action);
  const transactionType = amount
    ? i18next.t(
        `portfolio.transactions.portfolioTypes.${transactionTypeConst}`,
        {
          amount: formatMoneyNumeral(amount),
        },
      )
    : i18next.t(
        `portfolio.transactions.portfolioTypesNonAmount.${transactionTypeConst}`,
      );

  const statusCell = (
    <PendingTransactionCardStatus>
      {isMobile ? (
        <Tooltip text={startCase(status)}>
          <Status status={status} />
        </Tooltip>
      ) : (
        <Status status={status} />
      )}
    </PendingTransactionCardStatus>
  );

  const cancelCell = canCancel && (
    <PendingTransactionCardIcon
      onClick={(event) => event.stopPropagation()}
      role="presentation"
    >
      <Button
        css={pendingTransactionCancel}
        onClick={handleModalOpen}
        disabled={
          !canCancel ||
          !allowAccountChangesPermission ||
          (isTriggerOrder && isIneligibleToCancel)
        }
        variant="primaryOutlined"
        size="sm"
      >
        {i18next.t('common.cancel')}
      </Button>
    </PendingTransactionCardIcon>
  );

  return (
    <PendingTransactionCardContainer
      data-cy="list-item-pending-transaction"
      {...(onCardClick ? buttonizeDiv(onCardClick) : {})}
    >
      <PendingTransactionCardInfo>
        <PendingTransactionCardDate>
          <PendingTransactionCardMonth>
            {month.substring(0, 3)}
          </PendingTransactionCardMonth>
          <PendingTransactionCardDay>{day}</PendingTransactionCardDay>
        </PendingTransactionCardDate>
        <PendingTransactionCardTitle>
          {label ? (
            <PendingTransactionCardAction>{label}</PendingTransactionCardAction>
          ) : (
            action && (
              <PendingTransactionCardAction>
                {transactionType}
              </PendingTransactionCardAction>
            )
          )}
          {(product || vaultName) && (
            <PendingTransactionCardProduct>
              {product}
              {product && vaultName && ' '}
              {vaultName}
              {isSegregated && ` (${segregatedCaption})`}
            </PendingTransactionCardProduct>
          )}
        </PendingTransactionCardTitle>
      </PendingTransactionCardInfo>
      <PendingTransactionCardTrailing>
        {statusCell}
        <PendingTransactionCardActions>
          {cancelCell}
          <PendingTransactionCardArrow aria-hidden="true">
            {isTriggerOrder && <Caret />}
          </PendingTransactionCardArrow>
        </PendingTransactionCardActions>
      </PendingTransactionCardTrailing>
    </PendingTransactionCardContainer>
  );
};

PendingTransactionCard.propTypes = {
  month: PropTypes.string,
  day: PropTypes.string,
  amount: PropTypes.number,
  status: PropTypes.string,
  action: PropTypes.number,
  product: PropTypes.string,
  isSegregated: PropTypes.bool,
  segregatedCaption: PropTypes.string,
  vaultName: PropTypes.string,
  label: PropTypes.string,
  handleModalOpen: PropTypes.func,
  canCancel: PropTypes.bool,
  onCardClick: PropTypes.func,
  isTriggerOrder: PropTypes.bool,
};

export default PendingTransactionCard;
